/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { ApiDependenciesService } from './../../api/services/api-dependencies.service';
import { Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { SignalRError } from 'rxjs-signalr';
import { environment } from 'environments/environment';
import { Constants } from 'app/core/models/constants';

export class ConnectionStatusChangedMessage {
  public message: String;
  constructor(message: String) {
    this.message = message;
  }
}

export class ErrorMessage {
  public message: String;
  constructor(message: String) {
    this.message = message;
  }
}

export class MessageFromServer {
  constructor(public method: String, public data: any) {}
}

export class ConnectionStatusChanged extends Subject<ConnectionStatusChangedMessage> {}
export class ErrorOccurred extends Subject<ErrorMessage> {}
export class MessageReceivedFromServer extends Subject<MessageFromServer> {}

export abstract class SignalRService {
  // RxJS observables
  public onConnectionStatusChanged$ = new ConnectionStatusChanged();
  public onErrorOccurred$ = new ErrorOccurred();
  public onMessageReceivedFromServer$ = new MessageReceivedFromServer();

  /*
  This service will need to be registered as a provider.  When this was exercised at its introduction it was registered in app.module.ts

  This service can be injected in to components and the observables above can be subscribed to as per the syntax below.
  NOTE: in your component you must use the => syntax if you want to be able to reference the 'this' object from within the subscribed method.
  Otherwise typescript will not allow for the reevaluation of the _this object when transpiled to javascript.

  signalRService.onMessageReceivedFromServer$.subscribe((message: MessageFromServer)=>{
         this.receivedMessages = message.message;
    });
  */
  private connection: any;
  protected hub: any;
  constructor(
    hubName: string,
    url: string,
    protected dependencies: ApiDependenciesService
  ) {
    this.connection = $.hubConnection(`${url}/`);

    this.setQueryString(); // adds the authenticated user access token as a query string

    this.hub = this.connection.createHubProxy(hubName);

    // subscribe to status changes
    this.hub.state$?.subscribe((state) => {
      this.onConnectionStatusChanged$.next(
        new ConnectionStatusChangedMessage(state)
      );
    });

    // // subscribe to errors
    this.hub.error$?.subscribe((error: SignalRError) => {
      this.onErrorOccurred$.next(new ErrorMessage(error.message));
    });

    this.addServerPushMethods();

    this.hub.connection.logging = true;

    this.hub.connection
      .start()
      .done((data: any) => {
        console.log('Connected to Connection Hub');
      })
      .catch((error: any) => {
        console.log('Connection Hub error:' + error);
      });
  }

  // method to add hub specific messages that will be received from the server
  protected abstract addServerPushMethods();

  protected setQueryString() {
    const accessTokenFromCookie = localStorage.getItem(
      Constants.LocalStorage.KeyAccessToken
    );
    this.connection.qs = { access_token: accessTokenFromCookie };
  }

  public async sendMessage(data: string) {
    this.sendData('DataFromClient', data);
  }

  public async sendData(method: string, data: any) {
    if (this.isConnected) {
      this.sendDataOnConnectedHub(method, data);
    } else {
      console.debug('Cant send message. Not connected');
    }
  }

  public async reconnect() {
    this.hub.start();
  }

  public async sendDataOnConnectedHub(method: string, data: any) {
    console.debug('SignalR sending message [' + method + ']...');
    console.debug(data);
    this.hub.send(method, data);
  }

  public get isConnected(): boolean {
    return this.hub.connection.state === $.signalR.connectionState.connected;
  }
}

@Injectable()
export class NotificationHubService extends SignalRService {
  constructor(dependencies: ApiDependenciesService) {
    super('InAppNotificationHub', environment.notificationHubUrl, dependencies);
  }

  addServerPushMethods() {
    this.hub.on('SendNotification', (serverMessage) => {
      this.onMessageReceivedFromServer$.next(
        new MessageFromServer('SendNotification', serverMessage)
      );
    });
  }
}
